import React, { useRef, useState } from "react";
import { api, API, getToken } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, UploadCloud, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function BulkUpload() {
  const fileRef = useRef();
  const [mode, setMode] = useState("DRAFT");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const onFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("mode", mode);
      fd.append("file", file);
      const { data } = await api.post("/bulk-releases/import", fd);
      setResult(data);
      toast.success(`Created ${data.created} releases (${data.status})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-6" data-testid="bulk-upload-page">
      <div><h1 className="font-heading text-3xl font-bold tracking-tight">Bulk Release Upload</h1><p className="mt-1 text-muted-foreground">Import backlog releases for a label or solo artist from Excel/CSV.</p></div>
      <Card className="space-y-4 p-6">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label>Import Mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="mt-1 w-56" data-testid="bulk-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="DRAFT">DRAFT (review first)</SelectItem>
                <SelectItem value="LIVE">LIVE (backlog, already live)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <a href={`${API}/bulk-releases/template?auth=${getToken()}`} download><Button variant="secondary" data-testid="bulk-template-btn"><Download className="mr-2 h-4 w-4" />Template</Button></a>
          <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="bulk-upload-btn">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UploadCloud className="mr-2 h-4 w-4" />}Upload File
          </Button>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onFile} data-testid="bulk-file-input" />
        </div>
        <p className="text-xs text-muted-foreground">Group tracks under the same "Release Title". Artist &amp; Label are matched by name.</p>
        {result && <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm text-emerald-300" data-testid="bulk-result">Imported {result.created} release(s) as {result.status}.</div>}
      </Card>
    </div>
  );
}
